import React, { useEffect, useMemo, useState } from "react";
import { Grid, Layout, Menu, Tooltip } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AppstoreOutlined,
  CloudServerOutlined,
  CommentOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FileSearchOutlined,
  GiftOutlined,
  IdcardOutlined,
  KeyOutlined,
  LineChartOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  ProfileOutlined,
  RobotOutlined,
  SunOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button } from "./Common";
import { logout, selectUser } from "../store/authSlice";
import { useTheme } from "../context/ThemeContext";
import useAccess, { MODULES } from "../hooks/useAccess";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

// Navigation grouping is presentational only. Route keys and permission gates
// deliberately match App.js and useAccess exactly.
const NAV_GROUPS = [
  {
    key: "overview",
    label: "Overview",
    items: [
      {
        key: "",
        icon: <DashboardOutlined />,
        label: "Dashboard",
        module: null,
      },
    ],
  },
  {
    key: "customers-billing",
    label: "Customers & Billing",
    items: [
      {
        key: "/clients",
        icon: <UserOutlined />,
        label: "Clients",
        module: MODULES.CLIENTS,
      },
      {
        key: "/packages",
        icon: <GiftOutlined />,
        label: "Packages",
        module: MODULES.PACKAGES,
      },
      {
        key: "/subscription",
        icon: <ProfileOutlined />,
        label: "Subscription",
        module: MODULES.SUBSCRIPTIONS,
      },
      {
        key: "/payments",
        icon: <CreditCardOutlined />,
        label: "Payments",
        module: MODULES.PAYMENTS,
      },
    ],
  },
  {
    key: "ai-configuration",
    label: "AI & Configuration",
    items: [
      {
        key: "/agents",
        icon: <RobotOutlined />,
        label: "Agents",
        module: MODULES.AGENTS,
      },
      {
        key: "/llm-models",
        icon: <DatabaseOutlined />,
        label: "LLM Models",
        module: MODULES.LLM_MODELS,
      },
      {
        key: "/llm-keys",
        icon: <KeyOutlined />,
        label: "LLM Keys",
        module: MODULES.LLM_KEYS,
      },
      {
        key: "/apikeys",
        icon: <KeyOutlined />,
        label: "API Keys",
        module: MODULES.API_KEYS,
      },
      {
        key: "/tokenvalues",
        icon: <DollarOutlined />,
        label: "Token Values",
        module: MODULES.TOKEN_VALUES,
      },
    ],
  },
  {
    key: "insights-quality",
    label: "Insights & Quality",
    items: [
      {
        key: "/logs",
        icon: <FileSearchOutlined />,
        label: "Logs",
        module: MODULES.LOGS,
      },
      {
        key: "/feedbacks",
        icon: <CommentOutlined />,
        label: "Feedbacks",
        module: MODULES.FEEDBACKS,
      },
      {
        key: "/reports/cost-margin",
        icon: <LineChartOutlined />,
        label: "Cost & Margin",
        module: MODULES.REPORTS,
      },
    ],
  },
  {
    key: "platform-access",
    label: "Platform & Access",
    items: [
      {
        key: "/users",
        icon: <TeamOutlined />,
        label: "Users & Access",
        module: MODULES.USERS,
      },
      {
        key: "/plugins",
        icon: <AppstoreOutlined />,
        label: "Plugins",
        ownerAdminOnly: true,
      },
      {
        key: "/backups",
        icon: <CloudServerOutlined />,
        label: "Backup & Restore",
        ownerAdminOnly: true,
      },
      {
        key: "/redis",
        icon: <ThunderboltOutlined />,
        label: "Redis",
        ownerAdminOnly: true,
      },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap(({ items }) => items);

const resolveSelectedKey = (pathname) => {
  if (pathname === "/") return "";

  const matchingItems = ALL_ITEMS.filter(
    ({ key }) => key && (pathname === key || pathname.startsWith(key + "/"))
  ).sort((left, right) => right.key.length - left.key.length);

  return matchingItems[0]?.key ?? pathname;
};

const focusMenuTrigger = () => {
  document.querySelector(".app-header__trigger")?.focus();
};

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isBelowXl = !screens.xl;
  const { isDarkMode, toggleTheme } = useTheme();
  const { can, isUnrestricted, role } = useAccess();
  const user = useSelector(selectUser);

  useEffect(() => {
    setCollapsed(isBelowXl);
  }, [isBelowXl]);

  useEffect(() => {
    if (!isBelowXl || collapsed) return undefined;

    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setCollapsed(true);
      focusMenuTrigger();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [collapsed, isBelowXl]);

  const selectedKey = useMemo(
    () => resolveSelectedKey(location.pathname),
    [location.pathname]
  );

  const sectionLabel = useMemo(
    () => ALL_ITEMS.find(({ key }) => key === selectedKey)?.label ?? "",
    [selectedKey]
  );

  const menuItems = useMemo(
    () =>
      NAV_GROUPS.map((group) => {
        const children = group.items
          .filter(({ module, ownerAdminOnly }) =>
            ownerAdminOnly ? isUnrestricted : !module || can(module)
          )
          .map(({ key, icon, label }) => ({
            key,
            icon,
            label: collapsed ? null : label,
            title: collapsed ? label : undefined,
            className: "app-menu__item",
          }));

        if (!children.length) return null;

        return {
          type: "group",
          key: "group-" + group.key,
          label: collapsed ? null : group.label,
          children,
        };
      }).filter(Boolean),
    [collapsed, can, isUnrestricted]
  );

  const handleMenuClick = ({ key }) => {
    navigate(key || "/");
    if (isBelowXl) setCollapsed(true);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const openProfileWithKeyboard = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    navigate("/profile");
  };

  const closeOverlay = () => {
    setCollapsed(true);
    focusMenuTrigger();
  };

  return (
    <Layout className="app-layout">
      {isBelowXl && !collapsed && (
        <button
          type="button"
          className="app-sider__backdrop"
          onClick={closeOverlay}
          aria-label="Close navigation"
        />
      )}

      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme={isDarkMode ? "dark" : "light"}
        width={isBelowXl ? 288 : 224}
        collapsedWidth={isBelowXl ? 0 : 64}
        breakpoint="xl"
        onBreakpoint={(broken) => setCollapsed(broken)}
        className={
          "app-sider" + (isBelowXl ? " app-sider--overlay" : "")
        }
        aria-label="Back-office sidebar"
      >
        <div
          className={
            "app-sider__logo" +
            (collapsed ? " app-sider__logo--collapsed" : "")
          }
        >
          <img
            src="/ERP.ico"
            className="app-sider__logo-img app-sider__logo-img--symbol"
            alt={collapsed ? "Kubit AI" : ""}
          />
          {!collapsed && (
            <div className="app-sider__brand-copy">
              <span className="app-sider__brand-title">Kubit AI</span>
              <span className="app-sider__brand-subtitle">Integration</span>
            </div>
          )}
        </div>

        <nav
          id="backoffice-navigation"
          className="app-sider__scroll"
          aria-label="Back-office navigation"
        >
          <Menu
            mode="inline"
            items={menuItems}
            selectedKeys={[selectedKey]}
            onClick={handleMenuClick}
            inlineIndent={12}
            className={
              "app-menu" + (collapsed ? " app-menu--collapsed" : "")
            }
          />
        </nav>

        <div className="app-sider__footer">
          <Button
            variant="danger"
            block
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            aria-label="Log out"
          >
            {!collapsed && "Logout"}
          </Button>
        </div>
      </Sider>

      <Layout className="app-layout__inner">
        <Header className="app-header">
          <Button
            variant="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((current) => !current)}
            className="app-header__trigger"
            aria-label={collapsed ? "Open navigation" : "Close navigation"}
            aria-expanded={!collapsed}
            aria-controls="backoffice-navigation"
          />
          {sectionLabel && (
            <div className="app-header__title">
              <span className="app-header__title-text">{sectionLabel}</span>
            </div>
          )}
          <div className="app-header__spacer" />
          <div className="app-header__right">
            <Tooltip title="My profile & security">
              <Button
                variant="text"
                icon={<IdcardOutlined />}
                onClick={() => navigate("/profile")}
                aria-label="Open profile and security"
              />
            </Tooltip>
            <Tooltip title="Change password">
              <Button
                variant="text"
                icon={<LockOutlined />}
                onClick={() => navigate("/change-password")}
                aria-label="Change password"
              />
            </Tooltip>
            <Tooltip title={isDarkMode ? "Light mode" : "Dark mode"}>
              <Button
                variant="text"
                icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                className="theme-toggle"
                aria-label={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              />
            </Tooltip>
            {!isMobile && user?.username && (
              <>
                <div className="app-header__divider" />
                <div
                  className="app-header__user"
                  title={user.username}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/profile")}
                  onKeyDown={openProfileWithKeyboard}
                  aria-label={"Open profile for " + user.username}
                >
                  <div className="app-header__avatar">
                    {user.username.charAt(0)}
                  </div>
                  <div className="app-header__user-meta">
                    <span className="app-header__user-name">
                      {user.username}
                    </span>
                    {role && (
                      <span className="app-header__user-role">{role}</span>
                    )}
                  </div>
                </div>
              </>
            )}
            {isMobile && (
              <Button
                variant="secondary"
                onClick={handleLogout}
                icon={<LogoutOutlined />}
                aria-label="Log out"
              >
                <span className="sr-only">Logout</span>
              </Button>
            )}
          </div>
        </Header>

        <Content className="app-content">
          <div className="app-content__inner">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
